(() => {
  'use strict';

  if (window.themeAnalytics) return;

  const SECTION_SELECTOR = '[data-analytics-section]';
  const ITEM_SELECTOR = 'product-block[data-analytics-item-id]';
  const MIN_VISIBLE_DURATION = 1000;
  const MAX_ITEMS_PER_EVENT = 200;
  const COMPLETE_THE_LOOK_EVENT = 'complete-the-look:track';
  const COMPLETE_THE_LOOK_ATTRIBUTION_KEY = 'complete_the_look_attribution';
  const COMPLETE_THE_LOOK_ATTRIBUTION_TTL = 30 * 60 * 1000;
  const YMAL_EVENT = 'ymal:track';
  const YMAL_ATTRIBUTION_KEY = 'ymal_attribution';
  const YMAL_ATTRIBUTION_TTL = 30 * 60 * 1000;
  const ANALYTICS_SESSION_KEY = 'ggradual_analytics_session_id';
  const HEADER_NAV_SELECTOR = '.navigation__link[data-nav-item-id]';
  const HEADER_NAV_ATTRIBUTION_KEY = 'theme_analytics_navigation_source';
  const MODULE_SOURCE_KEY = 'theme_analytics_source';
  const MODULE_SOURCE_TTL = 30 * 60 * 1000;
  const sectionStates = new WeakMap();
  const observedSections = new WeakSet();
  const trackedItemListItems = new WeakSet();
  const observedItems = new WeakSet();
  let observer;
  let itemObserver;
  let contentObserver;

  const config = window.themeAnalyticsConfig || {};

  function hasAnalyticsConsent() {
    if (!config.requireAnalyticsConsent) return true;

    const privacy = window.Shopify && window.Shopify.customerPrivacy;
    if (!privacy || typeof privacy.analyticsProcessingAllowed !== 'function') return false;

    try {
      return privacy.analyticsProcessingAllowed();
    } catch (error) {
      return false;
    }
  }

  function loadConsentTrackingApi() {
    if (!config.requireAnalyticsConsent) return;
    if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function' || window.Shopify.customerPrivacy) return;

    window.Shopify.loadFeatures([{
      name: 'consent-tracking-api',
      version: '0.1',
      onLoad: (error) => {
        if (!error) {
          start();
          trackYmalProductView();
        }
      }
    }]);
  }

  function send(eventName, params) {
    if (!hasAnalyticsConsent()) return false;

    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'ggradual_ga4_event',
        ggradual_event_name: eventName,
        ggradual_event_params: Object.assign({
          page_type: config.pageType || '',
          page_path: window.location.pathname,
          currency: config.currency || undefined,
          debug_mode: new URLSearchParams(window.location.search).get('ga_debug') === '1' || undefined,
          tracking_source: 'theme_gtm'
        }, params)
      });
      return true;
    } catch (error) {
      return false;
    }
  }

  function isCompleteTheLook(section) {
    return section && section.dataset.analyticsSectionType === 'complete-the-look';
  }

  // 详情页 You May Also Like 使用独立 ymal_* 事件，不再进入通用模块和商品列表事件。
  function isProductRecommendations(section) {
    return section && section.dataset.analyticsSectionType === 'product-recommendations';
  }

  function analyticsSessionId() {
    try {
      const existing = sessionStorage.getItem(ANALYTICS_SESSION_KEY);
      if (existing) return existing;
      const created = window.crypto && typeof window.crypto.randomUUID === 'function'
        ? window.crypto.randomUUID()
        : String(Date.now()) + '-' + Math.random().toString(16).slice(2);
      sessionStorage.setItem(ANALYTICS_SESSION_KEY, created);
      return created;
    } catch (error) {
      return undefined;
    }
  }

  function ymalParams(params) {
    return Object.assign({ session_id: analyticsSessionId() }, params);
  }

  function ymalAttribution() {
    try {
      const attribution = JSON.parse(sessionStorage.getItem(YMAL_ATTRIBUTION_KEY));
      if (!attribution || Date.now() - attribution.timestamp > YMAL_ATTRIBUTION_TTL) {
        sessionStorage.removeItem(YMAL_ATTRIBUTION_KEY);
        return null;
      }
      return attribution;
    } catch (error) {
      return null;
    }
  }

  function rememberYmalAttribution(attribution) {
    try {
      sessionStorage.setItem(YMAL_ATTRIBUTION_KEY, JSON.stringify(attribution));
    } catch (error) {
      // 归因写入失败不得影响加购或跳转。
    }
  }

  // GA4 单个事件参数最长 100 字符；超长时按完整商品 ID 截断，张数仍用 recommendation_count。
  function limitRecommendationIds(value) {
    const ids = String(value || '');
    if (ids.length <= 100) return ids;
    const truncated = ids.slice(0, 100);
    const comma = truncated.lastIndexOf(',');
    return comma > 0 ? truncated.slice(0, comma) : truncated;
  }

  function trackYmal(event) {
    const detail = event.detail || {};
    if (!detail.eventName || !detail.params) {
      if (event.preventDefault) event.preventDefault();
      return;
    }
    const params = Object.assign({}, detail.params);
    if (params.recommendation_product_ids) {
      params.recommendation_product_ids = limitRecommendationIds(params.recommendation_product_ids);
    }
    if (!send(detail.eventName, ymalParams(params)) && event.preventDefault) event.preventDefault();
  }

  // 只有从 YMAL 点进对应商品页才发一次；标准 view_item 仍由像素另发。
  function trackYmalProductView() {
    const attribution = ymalAttribution();
    if (!attribution || attribution.product_view_sent) return;
    if (attribution.recommendation_path !== window.location.pathname) return;

    const sent = send('ymal_product_view', ymalParams({
      main_product_id: attribution.main_product_id,
      recommendation_product_id: attribution.recommendation_product_id,
      recommendation_position: attribution.recommendation_position,
      recommendation_source: attribution.recommendation_source || 'you_may_also_like'
    }));
    if (!sent) return;
    rememberYmalAttribution(Object.assign({}, attribution, { product_view_sent: true }));
  }

  function trackYmalAddToCart(event) {
    const target = event.target;
    if (!target || typeof target.closest !== 'function') return;

    const card = target.closest('product-block');
    const section = card && card.closest('.related-products[data-ymal]');
    if (section) {
      const productId = card.dataset.productId || card.dataset.analyticsItemId;
      if (!section.dataset.currentProductId || !productId) return;
      const detail = event.detail || {};
      const price = Number(detail.price);
      send('ymal_add_to_cart', ymalParams({
        main_product_id: section.dataset.currentProductId,
        recommendation_product_id: productId,
        recommendation_position: Number(card.dataset.ymalPosition) || undefined,
        recommendation_source: 'you_may_also_like',
        quantity: Number(detail.quantity) || 1,
        price: Number.isFinite(price) ? price : undefined
      }));
      return;
    }
    if (!target.closest('product-form')) return;

    const attribution = ymalAttribution();
    if (!attribution || attribution.recommendation_path !== window.location.pathname) return;

    const detail = event.detail || {};
    const price = Number(detail.price);
    const quantity = Number(detail.quantity) || 1;
    rememberYmalAttribution(Object.assign({}, attribution, {
      added: true,
      quantity,
      price: Number.isFinite(price) ? price : undefined
    }));
    send('ymal_add_to_cart', ymalParams({
      main_product_id: attribution.main_product_id,
      recommendation_product_id: attribution.recommendation_product_id,
      recommendation_position: attribution.recommendation_position,
      quantity,
      price: Number.isFinite(price) ? price : undefined
    }));
  }

  function trackYmalCheckout(event) {
    const control = event.target && event.target.closest && event.target.closest('button[name="checkout"], a[href*="/checkout"]');
    if (!control || control.dataset.ymalCheckoutTracked === 'true') return;

    const attribution = ymalAttribution();
    if (!attribution || !attribution.added) return;

    control.dataset.ymalCheckoutTracked = 'true';
    send('ymal_begin_checkout', ymalParams({
      main_product_id: attribution.main_product_id,
      recommendation_product_id: attribution.recommendation_product_id,
      recommendation_position: attribution.recommendation_position,
      quantity: attribution.quantity || 1,
      price: attribution.price
    }));
  }

  function completeTheLookAttribution() {
    try {
      const attribution = JSON.parse(sessionStorage.getItem(COMPLETE_THE_LOOK_ATTRIBUTION_KEY));
      if (!attribution || Date.now() - attribution.timestamp > COMPLETE_THE_LOOK_ATTRIBUTION_TTL) {
        sessionStorage.removeItem(COMPLETE_THE_LOOK_ATTRIBUTION_KEY);
        return null;
      }
      return attribution;
    } catch (error) {
      return null;
    }
  }

  function trackCompleteTheLook(event) {
    const detail = event.detail || {};
    if (!detail.eventName || !detail.params) return;
    send(detail.eventName, detail.params);
  }

  function trackCompleteTheLookAddToCart(event) {
    const target = event.target;
    if (!target || target.closest('complete-the-look') || !target.closest('product-form')) return;

    const attribution = completeTheLookAttribution();
    if (!attribution || attribution.recommendation_path !== window.location.pathname) return;

    const detail = event.detail || {};
    const price = Number(detail.price);
    const quantity = Number(detail.quantity) || 1;
    try {
      sessionStorage.setItem(COMPLETE_THE_LOOK_ATTRIBUTION_KEY, JSON.stringify(Object.assign({}, attribution, {
        added: true,
        quantity,
        price: Number.isFinite(price) ? price : undefined
      })));
    } catch (error) {
      // Attribution storage is optional and must not affect cart updates.
    }
    send('complete_the_look_add_to_cart', {
      main_product_id: attribution.main_product_id,
      recommendation_product_id: attribution.recommendation_product_id,
      quantity,
      price: Number.isFinite(price) ? price : undefined,
      recommendation_position: attribution.recommendation_position
    });
  }

  function trackCompleteTheLookCheckout(event) {
    const control = event.target && event.target.closest('button[name="checkout"], a[href*="/checkout"]');
    if (!control || control.dataset.completeTheLookCheckoutTracked === 'true') return;

    const attribution = completeTheLookAttribution();
    if (!attribution || !attribution.added) return;

    control.dataset.completeTheLookCheckoutTracked = 'true';
    send('complete_the_look_begin_checkout', {
      main_product_id: attribution.main_product_id,
      recommendation_product_id: attribution.recommendation_product_id,
      quantity: attribution.quantity || 1,
      price: attribution.price,
      recommendation_position: attribution.recommendation_position
    });
  }

  function text(value) {
    return (value || '').replace(/\s+/g, ' ').trim().slice(0, 100);
  }

  function isAnalyticsHidden(el) {
    if (!el) return true;
    if (el.hidden || (typeof el.matches === 'function' && el.matches('[hidden]'))) return true;
    return typeof el.closest === 'function' && !!el.closest('[hidden]');
  }

  function visibleItemNodes(section) {
    return Array.from(section.querySelectorAll(ITEM_SELECTOR)).filter((item) => !isAnalyticsHidden(item));
  }

  function storeModuleSource(params) {
    try {
      sessionStorage.setItem(MODULE_SOURCE_KEY, JSON.stringify(Object.assign({}, params, {
        timestamp: Date.now(),
        ttl_ms: MODULE_SOURCE_TTL
      })));
    } catch (error) {
      // Source attribution is optional and must not affect navigation.
    }
  }

  function searchTerm(value) {
    const term = text(value);
    return term && !/@|(?:\d[\s().-]*){7,}/.test(term) ? term : '';
  }

  function sectionMeta(section) {
    const sections = Array.from(document.querySelectorAll(SECTION_SELECTOR));
    const position = sections.indexOf(section) + 1;

    return {
      section_id: section.dataset.analyticsSectionId || section.id || '',
      section_type: section.dataset.analyticsSectionType || '',
      section_name: section.dataset.analyticsSectionName || section.dataset.analyticsSectionType || '',
      section_position: position > 0 ? position : undefined
    };
  }

  function getItemPayload(item, index) {
    const price = Number(item.dataset.analyticsItemPrice);
    const selectedOptions = item.querySelectorAll
      ? Array.from(item.querySelectorAll('.product-block-options[data-option-name]'))
      : [];
    const optionValue = (names) => {
      const option = selectedOptions.find((node) => names.includes(node.dataset.optionName.toLowerCase()));
      return option && option.querySelector('.product-block-options__item--active[data-option-item]')?.dataset.optionItem;
    };
    const color = optionValue(['color', 'colour']);
    const size = optionValue(['size']);
    const variantKnown = (!color || color === item.dataset.analyticsColor)
      && (!size || size === item.dataset.analyticsSize);
    const payload = {
      item_id: item.dataset.analyticsItemId,
      item_name: item.dataset.analyticsItemName,
      item_variant: variantKnown ? item.dataset.analyticsItemVariant || undefined : undefined,
      item_category: item.dataset.analyticsItemCategory || undefined,
      product_id: item.dataset.analyticsItemId,
      variant_id: variantKnown ? item.dataset.analyticsVariantId || undefined : undefined,
      sku: variantKnown ? item.dataset.analyticsSku || undefined : undefined,
      color: color || item.dataset.analyticsColor || undefined,
      size: size || item.dataset.analyticsSize || undefined,
      quantity: 1,
      index: index + 1
    };

    if (variantKnown && item.dataset.analyticsItemPrice && Number.isFinite(price)) payload.price = price;
    return payload;
  }

  function getItems(section, itemNodes, allItems) {
    const sectionItems = allItems || Array.from(section.querySelectorAll(ITEM_SELECTOR));
    const nodes = itemNodes || sectionItems;

    return nodes.map((item) => {
      const index = sectionItems.indexOf(item);
      return index >= 0 ? getItemPayload(item, index) : null;
    }).filter((item) => item && item.item_id && item.item_name);
  }

  function analyticsSelfCheck() {
    const item = {
      dataset: {
        analyticsItemId: 'self-check',
        analyticsItemName: 'self-check',
        analyticsItemPrice: '1'
      }
    };
    const valid = getItemPayload(item, 20).index === 21;

    console.assert(valid, 'collection item position tracking');
    return valid;
  }

  function headerNavigationSelfCheck() {
    const link = document.createElement('a');
    link.dataset.navItemId = 'self-check';
    link.dataset.navItemName = 'Self Check';
    link.dataset.navLevel = '2';
    link.dataset.navParentId = 'parent';
    link.dataset.navPath = 'Parent > Self Check';
    link.dataset.navDevice = 'desktop';
    link.dataset.navPosition = '2';
    const valid = link.dataset.navLevel === '2'
      && link.dataset.navPath === 'Parent > Self Check'
      && ['desktop', 'mobile'].includes(link.dataset.navDevice);

    console.assert(valid, 'header navigation metadata');
    return valid;
  }

  function sendItemList(section, visibleNodes) {
    if (!visibleNodes || !visibleNodes.length) return;
    const allItems = visibleItemNodes(section);
    if (!allItems.length) return;
    const newItems = visibleNodes.filter((item) => !trackedItemListItems.has(item) && !isAnalyticsHidden(item));

    for (let offset = 0; offset < newItems.length; offset += MAX_ITEMS_PER_EVENT) {
      const batchNodes = newItems.slice(offset, offset + MAX_ITEMS_PER_EVENT);
      const validNodes = batchNodes.filter((item) => (
        item.dataset.analyticsItemId && item.dataset.analyticsItemName
      ));
      const items = getItems(section, validNodes, allItems);
      if (!items.length) continue;

      const meta = sectionMeta(section);
      if (!send('view_item_list', Object.assign({}, meta, {
        item_list_id: meta.section_id,
        item_list_name: meta.section_name,
        items
      }))) {
        return;
      }

      validNodes.forEach((item) => {
        trackedItemListItems.add(item);
        itemObserver.unobserve(item);
        observedItems.delete(item);
      });
    }
  }

  function pause(section) {
    const state = sectionStates.get(section);
    if (!state || state.startedAt === null) return;

    state.visibleDuration += performance.now() - state.startedAt;
    state.startedAt = null;
  }

  function resume(section) {
    const state = sectionStates.get(section);
    if (!state || state.startedAt !== null || document.hidden || !state.viewSent || !state.isVisible) return;

    state.startedAt = performance.now();
  }

  function flushEngagement(section) {
    const state = sectionStates.get(section);
    if (!state || state.engagementSent || !state.viewSent) return;

    pause(section);
    const visibleDuration = Math.round(state.visibleDuration);
    if (visibleDuration < MIN_VISIBLE_DURATION) return;

    if (send('section_engagement', Object.assign({}, sectionMeta(section), {
      visible_duration_ms: visibleDuration
    }))) {
      state.engagementSent = true;
    }
  }

  function observeSection(section) {
    if (observedSections.has(section)) return;

    observedSections.add(section);
    sectionStates.set(section, {
      startedAt: null,
      visibleDuration: 0,
      viewSent: false,
      engagementSent: false,
      isVisible: false
    });
    observer.observe(section);
  }

  function initialize(root) {
    if (!observer || !root) return;

    const sections = [];
    if (root.matches && root.matches(SECTION_SELECTOR)) sections.push(root);
    if (root.querySelectorAll) sections.push(...root.querySelectorAll(SECTION_SELECTOR));

    sections.forEach((section) => {
      if (isCompleteTheLook(section) || isProductRecommendations(section)) return;
      if (!observedSections.has(section)) {
        observeSection(section);
        return;
      }

      const state = sectionStates.get(section);
      if (state && !state.viewSent) {
        observer.unobserve(section);
        observer.observe(section);
      }
    });

    const items = [];
    if (root.matches && root.matches(ITEM_SELECTOR)) items.push(root);
    if (root.querySelectorAll) items.push(...root.querySelectorAll(ITEM_SELECTOR));
    items.forEach((item) => {
      const itemSection = item.closest(SECTION_SELECTOR);
      if (trackedItemListItems.has(item) || isCompleteTheLook(itemSection) || isProductRecommendations(itemSection)) return;
      if (observedItems.has(item)) return;
      itemObserver.observe(item);
      observedItems.add(item);
    });
  }

  function trackClick(event) {
    if (!event.target || typeof event.target.closest !== 'function') return;

    const control = event.target.closest('a, button, [role="button"]');
    if (!control || isAnalyticsHidden(control)) return;

    const section = control.closest(SECTION_SELECTOR);
    if (!section) return;
    if (isCompleteTheLook(section) || isProductRecommendations(section)) return;

    const meta = sectionMeta(section);
    const productBlock = control.closest(ITEM_SELECTOR);
    const label = control.dataset.analyticsLabel || control.getAttribute('aria-label') || text(control.textContent);

    if (control.matches('[data-collection-infinite-scroll]')) return;

    if (control.closest('.amazon-button-block')) {
      send('amazon_click', Object.assign({}, meta, {
        element_label: label || 'Buy on Amazon',
        destination_url: control.href || ''
      }));
      return;
    }

    send('section_click', Object.assign({}, meta, {
      element_type: control.tagName.toLowerCase(),
      element_label: label || undefined,
      destination_url: control.href || undefined
    }));
    storeModuleSource({
      section_id: meta.section_id,
      section_name: meta.section_name,
      section_type: meta.section_type,
      destination_url: control.href || undefined
    });

    if (!productBlock || isAnalyticsHidden(productBlock) || !control.matches('a.product-link, a.quickbuy-toggle')) return;

    const allItems = visibleItemNodes(section);
    const itemIndex = allItems.indexOf(productBlock);
    const selectedItem = itemIndex >= 0
      ? getItemPayload(productBlock, itemIndex)
      : null;
    if (!selectedItem) return;

    const afterLoadMore = section.dataset.afterLoadMore === 'true';
    const loadMoreClickNumber = Number(section.dataset.loadMoreClickNumber);
    const itemSelected = send('select_item', Object.assign({}, meta, {
      item_list_id: meta.section_id,
      item_list_name: meta.section_name,
      after_load_more: afterLoadMore ? 'true' : 'false',
      load_more_click_number: afterLoadMore && Number.isFinite(loadMoreClickNumber)
        ? String(loadMoreClickNumber)
        : undefined,
      items: [selectedItem]
    }));

    if (!itemSelected) return;

    storeModuleSource({
      section_id: meta.section_id,
      section_name: meta.section_name,
      section_type: meta.section_type,
      item_id: selectedItem.item_id,
      destination_url: control.href || undefined
    });
  }

  function trackHeaderNavigation(event) {
    if (!event.target || typeof event.target.closest !== 'function') return;

    const control = event.target.closest(HEADER_NAV_SELECTOR);
    if (!control || !control.closest('main-navigation, #proxy-nav, .mobile-navigation-drawer')) return;

    const style = window.getComputedStyle(control);
    if (style.display === 'none' || style.visibility === 'hidden' || !control.getClientRects().length) return;

    const destinationUrl = control.href || control.dataset.navDestinationUrl || '';
    let collectionHandle;
    if (destinationUrl) {
      try {
        const pathSegments = new URL(destinationUrl, window.location.origin || window.location.href || 'https://example.com/').pathname.split('/').filter(Boolean);
        const collectionIndex = pathSegments.indexOf('collections');
        if (collectionIndex >= 0 && pathSegments[collectionIndex + 1]) {
          collectionHandle = decodeURIComponent(pathSegments[collectionIndex + 1]);
        }
      } catch (error) {
        // Collection metadata is optional and must not affect navigation.
      }
    }

    const params = {
      nav_item_id: control.dataset.navItemId,
      nav_item_name: control.dataset.navItemName || text(control.textContent),
      nav_level: control.dataset.navLevel,
      nav_parent_id: control.dataset.navParentId || undefined,
      nav_path: control.dataset.navPath || undefined,
      nav_device: control.dataset.navDevice || (window.matchMedia('(max-width: 767px)').matches ? 'mobile' : 'desktop'),
      nav_position: control.dataset.navPosition,
      destination_url: destinationUrl || undefined,
      collection_handle: collectionHandle || undefined,
      collection_name: collectionHandle ? (control.dataset.navItemName || text(control.textContent)) : undefined
    };

    if (!send('header_navigation_click', params)) return;

    try {
      sessionStorage.setItem(HEADER_NAV_ATTRIBUTION_KEY, JSON.stringify(Object.assign({}, params, {
        timestamp: Date.now()
      })));
    } catch (error) {
      // Attribution storage is optional and must not affect navigation.
    }
  }

  function trackHelpSearch(event) {
    const form = event.target;
    if (!form || !form.matches || !form.matches('form[data-analytics-help-search]')) return;

    const term = searchTerm(form.elements && form.elements.q && form.elements.q.value);
    if (!term) return;

    send('search', {
      search_term: term,
      search_context: form.dataset.analyticsSearchContext || 'help_center'
    });
  }

  function searchSelfCheck() {
    const valid = searchTerm(' return policy ') === 'return policy';
    const email = searchTerm('help@example.com') === '';
    const number = searchTerm('1234567') === '';
    console.assert(valid, 'normal search term');
    console.assert(email, 'email not tracked');
    console.assert(number, 'long number not tracked');
    return valid && email && number;
  }

  function start() {
    if (!itemObserver) {
      itemObserver = new IntersectionObserver((entries) => {
        const visibleBySection = new Map();
        entries.forEach((entry) => {
          if (!entry.isIntersecting || entry.intersectionRatio < 0.5 || document.hidden) return;
          const item = entry.target;
          const section = item.closest(SECTION_SELECTOR);
          if (!section || isCompleteTheLook(section) || isProductRecommendations(section)) return;
          if (!visibleBySection.has(section)) visibleBySection.set(section, []);
          visibleBySection.get(section).push(item);
        });
        visibleBySection.forEach((items, section) => sendItemList(section, items));
      }, { threshold: 0.5 });
    }
    if (!observer) {
      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const section = entry.target;
          const state = sectionStates.get(section);
          if (!state) return;

          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            state.isVisible = true;
            if (!state.viewSent) {
              state.viewSent = send('section_view', sectionMeta(section));
            }
            if (state.viewSent) {
              resume(section);
            }
          } else {
            state.isVisible = false;
            pause(section);
            // Flush engagement as soon as a visitor leaves the module. This
            // makes the dwell-time event useful even when the page is not
            // unloaded (for example, during normal scrolling).
            flushEngagement(section);
          }
        });
      }, { threshold: [0, 0.5] });
    }

    initialize(document);
  }

  function watchDynamicContent() {
    if (contentObserver || !document.body) return;

    contentObserver = new MutationObserver((records) => {
      records.forEach((record) => {
        if (record.type === 'attributes') {
          const target = record.target;
          if (!target || target.nodeType !== Node.ELEMENT_NODE) return;
          const parentSection = target.closest && target.closest(SECTION_SELECTOR);
          if (parentSection) initialize(parentSection);
          return;
        }

        record.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;

          initialize(node);

          const parentSection = node.closest && node.closest(SECTION_SELECTOR);
          if (parentSection) initialize(parentSection);
        });
      });
    });
    contentObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['hidden']
    });
  }

  document.addEventListener('click', trackClick, true);
  document.addEventListener('click', trackHeaderNavigation, true);
  document.addEventListener('click', trackCompleteTheLookCheckout, true);
  document.addEventListener('click', trackYmalCheckout, true);
  document.addEventListener('on:cart:add', trackCompleteTheLookAddToCart);
  document.addEventListener('on:cart:add', trackYmalAddToCart);
  document.addEventListener(COMPLETE_THE_LOOK_EVENT, trackCompleteTheLook);
  document.addEventListener(YMAL_EVENT, trackYmal);
  document.addEventListener('submit', trackHelpSearch, true);
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const sections = [];
    if (event.target?.matches?.(SECTION_SELECTOR)) sections.push(event.target);
    event.target?.querySelectorAll(SECTION_SELECTOR).forEach((section) => sections.push(section));
    sections.forEach((section) => {
      flushEngagement(section);
      observer?.unobserve(section);
      observedSections.delete(section);
      sectionStates.delete(section);
    });
    event.target?.querySelectorAll(ITEM_SELECTOR).forEach((item) => {
      itemObserver?.unobserve(item);
      observedItems.delete(item);
    });
  });
  document.addEventListener('visitorConsentCollected', () => {
    if (!hasAnalyticsConsent()) return;
    trackYmalProductView();
    document.querySelectorAll(ITEM_SELECTOR).forEach((item) => {
      if (trackedItemListItems.has(item)) return;
      itemObserver?.unobserve(item);
      observedItems.delete(item);
    });
    start();
  });
  document.addEventListener('visibilitychange', () => {
    document.querySelectorAll(SECTION_SELECTOR).forEach((section) => {
      if (document.hidden) {
        pause(section);
      } else {
        resume(section);
      }
    });
  });
  window.addEventListener('pagehide', () => {
    document.querySelectorAll(SECTION_SELECTOR).forEach(flushEngagement);
  });

  function boot() {
    start();
    watchDynamicContent();
    loadConsentTrackingApi();
    trackYmalProductView();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
  window.addEventListener('load', boot, { once: true });

  if (new URLSearchParams(window.location.search).get('ga_debug') === '1') {
    analyticsSelfCheck();
    headerNavigationSelfCheck();
  }

  (window.completeTheLookAnalyticsQueue || []).splice(0).forEach(trackCompleteTheLook);
  (window.ymalAnalyticsQueue || []).splice(0).forEach((detail) => trackYmal({ detail }));

  window.themeAnalytics = {
    initialize,
    send,
    hasAnalyticsConsent,
    sendItemList,
    searchSelfCheck,
    headerNavigationSelfCheck,
    isAnalyticsHidden,
    visibleItemNodes
  };
})();
